import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { SearchInput } from "./SearchInput";
import { WordSearchTabs } from "./WordSearchTabs";
import { QuestionSearchResults } from "./QuestionSearchResults";
import { ClueSuggestionForm } from "./ClueSuggestionForm";
import { ForumContent } from "./forum/ForumContent";
import { CombinedSearchPro } from "./CombinedSearchPro";
import { AnagramSearch } from "./AnagramSearch";
import { CryptoSolver } from "./CryptoSolver";
import { useWordSearch } from "@/hooks/useWordSearch";

type TabType = "question" | "word" | "combined" | "anagram" | "krypto" | "forum";

export const SearchTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<TabType>(
    tabParam === "forum" || tabParam === "question" || tabParam === "combined" || tabParam === "anagram" || tabParam === "krypto"
      ? tabParam
      : "word",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const {
    searchWords,
    questionResults,
    isLoading,
    error,
    clearResults,
    sortOption,
    changeSortOption,
    selectedLength,
    setSelectedLength,
  } = useWordSearch();

  useEffect(() => {
    if (
      tabParam === "forum" ||
      tabParam === "question" ||
      tabParam === "word" ||
      tabParam === "combined" ||
      tabParam === "anagram" ||
      tabParam === "krypto"
    ) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleQuestionSearch = () => {
    if (!searchQuery.trim()) {
      return;
    }
    searchWords(searchQuery, "question", selectedLength);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams(tab === "word" ? {} : { tab });
    if (tab !== "forum") {
      clearResults();
    }
  };

  const handleWordResultClick = (word: string) => {
    // Switch to question tab and search for the word
    setActiveTab("question");
    setSearchQuery(word);
    clearResults();
    // Trigger search after state update
    setTimeout(() => {
      searchWords(word, "question");
    }, 0);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Tabs - styled like original site */}
      <div className="flex gap-1 mb-0 overflow-hidden">
        <TabButton active={activeTab === "question"} onClick={() => handleTabChange("question")} label="Sök ledtråd" />
        <TabButton active={activeTab === "word"} onClick={() => handleTabChange("word")} label="Sök ord" />
        <TabButton
          active={activeTab === "combined"}
          onClick={() => handleTabChange("combined")}
          label="Avancerad sökning"
        />
        <TabButton active={activeTab === "anagram"} onClick={() => handleTabChange("anagram")} label="Anagram" />
        <TabButton active={activeTab === "krypto"} onClick={() => handleTabChange("krypto")} label="Kryptokryss" />
        <TabButton active={activeTab === "forum"} onClick={() => handleTabChange("forum")} label="Forum" />
      </div>

      {/* Content area with border */}
      <div className="border border-border rounded-b-lg rounded-tr-lg bg-card p-6">
        {activeTab === "word" ? (
          <WordSearchTabs onResultClick={handleWordResultClick} />
        ) : activeTab === "question" ? (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-foreground mb-1">Sök korsordsfråga</h2>
                <p className="text-sm text-muted-foreground">
                  Skriv in frågan från korsordet för att hitta möjliga svar
                </p>
              </div>
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="T.ex. huvudstad i sverige..."
                onSearch={handleQuestionSearch}
                showLengthFilter={true}
                selectedLength={selectedLength}
                onLengthChange={setSelectedLength}
              />
              <QuestionSearchResults
                results={questionResults}
                isLoading={isLoading}
                error={error}
                sortOption={sortOption}
                onSortChange={changeSortOption}
                searchTerm={searchQuery}
              />
            </div>
            <ClueSuggestionForm />
          </div>
        ) : activeTab === "combined" ? (
          <CombinedSearchPro />
        ) : activeTab === "anagram" ? (
          <AnagramSearch />
        ) : activeTab === "krypto" ? (
          <CryptoSolver />
        ) : (
          <ForumContent />
        )}
      </div>
    </div>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

const TabButton = ({ active, onClick, label }: TabButtonProps) => (
  <button
    onClick={onClick}
    className={cn(
      "px-6 py-3 text-sm font-medium transition-all duration-150 rounded-t-lg border border-b-0",
      active
        ? "bg-card text-foreground border-border -mb-px"
        : "bg-muted text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/80",
    )}
  >
    {label}
  </button>
);
